<script setup lang="ts">
import { ref, computed, watch, onMounted, onUnmounted } from 'vue'
import { useField } from 'vee-validate'
import { UploadCloud, FileText, Eye, Loader2, Trash2, AlertCircle } from 'lucide-vue-next'
import type { FormField } from '../../types/formTypeStructure'
import FormFieldWrapper from '../../FormFieldWrapper.vue'
import { useFileBucketManager } from '../../hooks/useFileBucketManager'
import { extractFileNameFromUrl } from '../../utils/fileName'
import { getLocalizedText } from '../../utils/formHelpers'

interface Props {
  field: FormField & { fieldType: 'PDF' }
  name?: string
  currentLang?: string
  defaultValue?: string
  className?: string
}

interface UploadedPDFInfo {
  url: string
  name: string
  size?: number
  bucketFilename?: string
}

type PreviewStatus = 'idle' | 'loading' | 'ready' | 'error'

const props = defineProps<Props>()
const fieldName = props.name ?? props.field.fieldName.toString()
const label = computed(() => getLocalizedText(props.field.label, props.currentLang ?? 'fr'))
const isRequired = computed(() => props.field.validations?.some(v => v.validationType === 'required'))

const {
  value,
  errorMessage: fieldErrorMessage,
  handleChange,
  setValue,
  setTouched
} = useField<string | null>(fieldName, undefined, {
  initialValue: props.defaultValue ?? null
})

const dragActive = ref(false)
const uploadedPDF = ref<UploadedPDFInfo | null>(null)
const previewUrl = ref<string | null>(null)
const previewStatus = ref<PreviewStatus>('idle')
const previewMessage = ref<string | null>(null)
const errorMessage = ref<string | null>(null)
const fileInputRef = ref<HTMLInputElement | null>(null)
const didInit = ref(false)
const previousPreviewRef = ref<string | null>(null)

const fileBucketManager = computed(() => 
  props.field.fileToBucketManage
    ? useFileBucketManager(props.field.fileToBucketManage)
    : null
)

const hasUploadSupport = computed(() => fileBucketManager.value?.hasUploadSupport ?? false)
const hasDeleteSupport = computed(() => fileBucketManager.value?.hasDeleteSupport ?? false)
const isUploading = computed(() => fileBucketManager.value?.isUploading ?? false)
const isDeleting = computed(() => fileBucketManager.value?.isDeleting ?? false)
const bucketError = computed(() => fileBucketManager.value?.error ?? null)

const effectiveError = computed(() => errorMessage.value ?? bucketError.value ?? fieldErrorMessage.value)

const clearPreview = () => {
  if (previousPreviewRef.value && previousPreviewRef.value.startsWith('blob:')) {
    URL.revokeObjectURL(previousPreviewRef.value)
  }
  previousPreviewRef.value = null
  previewUrl.value = null
  previewStatus.value = 'idle'
  previewMessage.value = null
}

const setPreviewFromBlob = (blob: Blob) => {
  clearPreview()
  const objectUrl = URL.createObjectURL(blob)
  previousPreviewRef.value = objectUrl
  previewUrl.value = objectUrl
  previewStatus.value = 'ready'
  previewMessage.value = null
}

const loadPreviewFromRemote = async (url: string) => {
  if (!url) {
    clearPreview()
    return
  }
  previewStatus.value = 'loading'
  previewMessage.value = null
  try {
    const response = await fetch(url, { mode: 'cors' })
    if (!response.ok) {
      throw new Error(response.statusText)
    }
    const blob = await response.blob()
    const contentTypeHeader = response.headers.get('Content-Type')?.toLowerCase() ?? ''
    const blobType = blob.type?.toLowerCase() ?? ''
    const fileName = extractFileNameFromUrl(url)?.toLowerCase() ?? url.split('?')[0].toLowerCase()

    const isProbablyPdf = contentTypeHeader.includes('pdf') || blobType.includes('pdf') || fileName.endsWith('.pdf')

    if (!isProbablyPdf) {
      throw new Error('Le fichier distant n\'est pas un PDF.')
    }

    const normalizedBlob = blobType.includes('pdf') ? blob : new Blob([blob], { type: 'application/pdf' })

    setPreviewFromBlob(normalizedBlob)
  } catch (error) {
    clearPreview()
    previewStatus.value = 'error'
    previewMessage.value = 'Prévisualisation impossible. Utilisez le bouton « Ouvrir » pour consulter le PDF.'
    console.warn('[PDFFormField] Preview fetch failed:', error)
  }
}

// Initialiser avec defaultValue
watch(() => props.defaultValue, (newValue) => {
  if (typeof newValue === 'string' && newValue.trim().length > 0) {
    uploadedPDF.value = {
      url: newValue,
      name: extractFileNameFromUrl(newValue) ?? newValue,
      bucketFilename: extractFileNameFromUrl(newValue) ?? undefined,
    }
    previewStatus.value = 'loading'
    previewMessage.value = null
    loadPreviewFromRemote(newValue)
    if (!didInit.value) {
      setValue(newValue, false)
      didInit.value = true
    }
  } else {
    clearPreview()
    uploadedPDF.value = null
  }
}, { immediate: true })

onUnmounted(() => {
  if (previousPreviewRef.value && previousPreviewRef.value.startsWith('blob:')) {
    URL.revokeObjectURL(previousPreviewRef.value)
  }
})

const formatFileSize = (bytes: number) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const sizes = ['Bytes', 'KB', 'MB', 'GB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i]
}

const handleDrag = (e: DragEvent) => {
  e.preventDefault()
  e.stopPropagation()
  dragActive.value = e.type === 'dragenter' || e.type === 'dragover'
}

const handleDrop = (e: DragEvent) => {
  e.preventDefault()
  e.stopPropagation()
  dragActive.value = false
  if (e.dataTransfer?.files && e.dataTransfer.files.length > 0) {
    handleFile(e.dataTransfer.files[0])
  }
}

const handleFile = async (file: File) => {
  // Vérifier que c'est bien un PDF
  if (file.type !== 'application/pdf') {
    alert('Veuillez sélectionner un fichier PDF valide')
    return
  }

  if (!hasUploadSupport.value) {
    errorMessage.value = 'Configuration d\'upload manquante pour ce champ (fileToBucketManage).'
    return
  }

  fileBucketManager.value?.resetError()
  errorMessage.value = null

  setPreviewFromBlob(file)
  previewStatus.value = 'ready'
  previewMessage.value = null

  try {
    const result = await fileBucketManager.value!.uploadFile({ file })
    uploadedPDF.value = {
      url: result.url,
      name: result.originalName ?? result.filename ?? extractFileNameFromUrl(result.url) ?? file.name,
      size: file.size,
      bucketFilename: result.filename ?? extractFileNameFromUrl(result.url),
    }
    setValue(result.url, true)
    setTouched(true)
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Échec de l\'upload du PDF.'
    errorMessage.value = message
    clearPreview()
  }
}

const handleInputChange = (e: Event) => {
  const target = e.target as HTMLInputElement
  if (target.files && target.files.length > 0) {
    handleFile(target.files[0])
  }
}

const removePDF = async () => {
  fileBucketManager.value?.resetError()
  errorMessage.value = null

  if (uploadedPDF.value && hasDeleteSupport.value) {
    try {
      await fileBucketManager.value!.deleteFile({
        filename: uploadedPDF.value.bucketFilename ?? extractFileNameFromUrl(uploadedPDF.value.url ?? '') ?? undefined,
        fileUrl: uploadedPDF.value.url,
      })
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Échec de la suppression du fichier distant.'
      errorMessage.value = message
      return
    }
  }

  uploadedPDF.value = null
  if (previewUrl.value && previewUrl.value.startsWith('blob:')) {
    URL.revokeObjectURL(previewUrl.value)
  }
  previewUrl.value = null
  previousPreviewRef.value = null
  setValue(null, true)
  setTouched(true)
  if (fileInputRef.value) {
    fileInputRef.value.value = ''
  }
}

const openPDF = () => {
  if (uploadedPDF.value?.url) {
    window.open(uploadedPDF.value.url, '_blank')
  }
}
</script>

<template>
  <div class="pdf-field-container">
    <!-- Label dupliqué au-dessus -->
    <div class="pdf-field-label-container">
      <span class="pdf-field-label-text">
        {{ label }}
        <AlertCircle
          v-if="isRequired"
          :size="16"
          class="inline text-red-500 ml-2"
        />
      </span>
    </div>

    <FormFieldWrapper :field="props.field" :current-lang="props.currentLang">
      <div class="space-y-4">
        <!-- Zone de drop -->
        <div
          v-if="!uploadedPDF && !previewUrl"
          :class="[
            'relative border-2 border-dashed rounded-lg cursor-pointer transition-all duration-300 ease-in-out',
            dragActive
              ? 'border-[#04406E] bg-[#e6f0f5] scale-[1.02]'
              : 'border-gray-300 hover:border-[#04406E] hover:bg-gray-50',
            props.className,
            isUploading && 'pointer-events-none opacity-60'
          ]"
          @dragenter="handleDrag"
          @dragleave="handleDrag"
          @dragover="handleDrag"
          @drop="handleDrop"
          @click="fileInputRef?.click()"
        >
          <input
            ref="fileInputRef"
            type="file"
            accept="application/pdf"
            class="hidden"
            @change="handleInputChange"
          />

          <div class="p-8 text-center">
            <div class="flex flex-col items-center space-y-4">
              <div
                :class="[
                  'p-3 rounded-full transition',
                  dragActive
                    ? 'bg-[#04406E] text-white'
                    : 'bg-gray-100 text-[#04406E] hover:bg-[#e6f0f5]'
                ]"
              >
                <FileText :size="24" />
              </div>

              <div>
                <p class="text-base font-medium text-gray-700 mb-1">
                  {{ dragActive ? 'Déposez votre PDF ici' : 'Glissez-déposez votre PDF' }}
                </p>
                <p class="text-sm text-gray-500">
                  ou
                  <span class="text-[#04406E] font-medium hover:underline">
                    cliquez pour parcourir
                  </span>
                </p>
                <p class="text-xs text-gray-400 mt-2">
                  Format accepté : PDF uniquement
                </p>
              </div>
            </div>
          </div>
        </div>

        <!-- Preview du PDF -->
        <div
          v-if="uploadedPDF || previewUrl || previewStatus === 'loading' || previewStatus === 'error'"
          class="space-y-3"
        >
          <!-- Boutons d'action -->
          <div class="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg">
            <div class="flex items-center space-x-3">
              <div class="p-2 bg-red-50 rounded-lg">
                <FileText :size="20" class="text-red-600" />
              </div>
              <div class="flex-1 min-w-0">
                <p class="font-medium text-gray-800 truncate">
                  {{ uploadedPDF?.name }}
                </p>
                <p v-if="uploadedPDF?.size || previewUrl" class="text-sm text-gray-500">
                  {{ uploadedPDF?.size ? formatFileSize(uploadedPDF.size) : '' }}
                </p>
              </div>
            </div>
            <div class="flex space-x-2">
              <button
                v-if="uploadedPDF"
                type="button"
                @click="openPDF"
                class="p-2 hover:bg-[#e6f0f5] rounded-lg transition-colors"
                title="Ouvrir dans un nouvel onglet"
              >
                <Eye :size="20" class="text-[#04406E]" />
              </button>
              <button
                type="button"
                @click="removePDF"
                :disabled="isDeleting"
                class="p-2 hover:bg-red-50 rounded-lg transition-colors disabled:opacity-60"
                title="Supprimer"
              >
                <Loader2 v-if="isDeleting" :size="20" class="animate-spin text-red-500" />
                <Trash2 v-else :size="20" class="text-red-500" />
              </button>
            </div>
          </div>

          <div
            v-if="previewStatus === 'loading'"
            class="border border-dashed border-gray-200 rounded-lg bg-white p-6 text-center text-sm text-gray-500 flex flex-col items-center gap-3"
          >
            <Loader2 :size="24" class="animate-spin text-[#04406E]" />
            <span>Génération de l'aperçu en cours…</span>
          </div>

          <div
            v-else-if="previewUrl"
            class="border border-gray-200 rounded-lg overflow-hidden bg-gray-50"
          >
            <iframe
              :src="previewUrl"
              class="w-full h-96"
              title="Aperçu PDF"
            />
          </div>

          <div
            v-else
            class="border border-dashed border-gray-200 rounded-lg bg-white p-6 text-center text-sm text-gray-500"
          >
            {{ previewMessage ?? 'Aperçu indisponible. Utilisez le bouton « Ouvrir » pour consulter le PDF.' }}
          </div>
        </div>

        <div
          v-if="effectiveError"
          class="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-600"
        >
          {{ effectiveError }}
        </div>
      </div>
    </FormFieldWrapper>
  </div>
</template>

<style scoped>
.pdf-field-container {
  position: relative;
  display: block;
  width: 100%;
}

.pdf-field-label-container {
  position: relative;
  display: block;
  margin-bottom: 0.75rem;
  z-index: 100;
}

.pdf-field-label-text {
  display: block;
  font-size: 1.125rem;
  font-weight: 700;
  color: #333333;
  line-height: 1.2;
  margin: 0;
  padding: 0;
  position: static !important;
  transform: none !important;
  top: auto !important;
  left: auto !important;
  right: auto !important;
  bottom: auto !important;
  height: auto !important;
  overflow: visible !important;
  text-align: left !important;
  text-overflow: unset !important;
  white-space: normal !important;
  pointer-events: auto !important;
  border: none !important;
  transform-origin: unset !important;
  transition: none !important;
}
</style>

